/* eslint-disable react/display-name */
import React, { memo } from 'react';
import { isEmpty } from 'lodash-es';
import { extractAndValidateJson } from '../../utils';
import AgentEchart from '../AgentEchart';

/**
 * Markdown 节点属性接口
 */
interface IMarkdownNode {
  tagName: string;
  [key: string]: any;
}

/**
 * Rest Props 接口
 */
interface IRestProps {
  node?: IMarkdownNode;
  children?: string;
  [key: string]: any;
}

/**
 * 答案任务项接口
 */
interface IAnswerTask {
  type: string;
  value?: any[];
}

/**
 * Echarts 图表渲染组件 Props 接口
 */
interface IEchartsChartRendererProps {
  /** Markdown 解析后的节点属性 */
  restProps: IRestProps;
  /** 目标标签名称（ech） */
  tagName: string;
  /** 答案数据数组 */
  answer: IAnswerTask[];
  /** 已解析的 data_id 集合引用，用于流式输出去重 */
  parsedDataIdsRef: React.MutableRefObject<Set<string>>;
}

/**
 * Echarts 图表渲染组件（可复用）
 *
 * 用于处理 Markdown 中的 ech 标签，从 biData 数据源中按 data_id 查找
 * 类型为 echarts_option 的数据，并渲染为 AgentEchart 图表。
 * 支持流式输出场景下的去重机制，避免相同 data_id 的图表重复渲染。
 *
 * @param props - 组件属性
 * @returns AgentEchart 图表组件或 null
 */
export const EchartsChartRenderer = memo(
  (props: IEchartsChartRendererProps) => {
    const { restProps, tagName, answer, parsedDataIdsRef } = props;

    // 1、判断是否是 echarts 占位符，如果不是则提前返回 null
    if (restProps?.node?.tagName !== tagName) {
      return null;
    }
    const echartsInfo = extractAndValidateJson(restProps?.children) || {};
    const { data_id } = echartsInfo;
    // 2、兜底：没有 data_id 则返回 null
    if (isEmpty(answer) || !data_id) return null;
    // 3、检查该 data_id 是否已经解析过，如果是则返回 null（流式输出去重）
    if (parsedDataIdsRef.current.has(data_id)) {
      return null;
    }
    // 4、从 biData 数据源中按 data_id 查找数据
    const answerBiData = answer
      ?.filter((task: { type: string; }) => task?.type === 'biData')
      .flatMap((item: any) => item.value);
    const dataSource = answerBiData?.find(
      (item: { data_id: string; }) => item.data_id === data_id,
    );
    // 5、仅渲染类型为 echarts_option 的数据源
    if (isEmpty(dataSource) || dataSource?.data?.type !== 'echarts_option') return null;
    // 6、标记该 data_id 已解析，后续相同 data_id 不再解析
    parsedDataIdsRef.current.add(data_id);

    return (
      <div>
        <AgentEchart value={dataSource.data.value} />
      </div>
    );
  },
  (prevProps: IEchartsChartRendererProps, nextProps: IEchartsChartRendererProps) => {
    // 自定义比较函数：仅当 data_id 相同时才跳过更新
    const getDataId = (props: IEchartsChartRendererProps): string | undefined =>
      extractAndValidateJson(props.restProps?.children)?.data_id;

    return getDataId(prevProps) === getDataId(nextProps);
  },
);
